import React from 'react'
import { format } from 'date-fns'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import {
    AllEvents,
    createEditionSelectItemEvent,
    Product,
    scaleImage,
    WebLink,
} from '@news-mono/web-common'
import { EditionsNightlyButtons } from './Components/EditionsNightlyButtons'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionHeading,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import {
    HeaderContainer,
    StyledDivider,
    StyledNightlyEditionButtonContainerLeft,
    StyledNightlyEditionContentHome,
    StyledNightlyEditionPictureRight,
    StyledNightlyEditionTextContainerLeft,
} from './SpecialEditionHome.styled'
import { HeaderPrefix } from '../SpecialEdition/SpecialEdition.styled'
import { breakpointMax, breakpoint } from '../../../__styling'
import { EditionPublication } from '@west-australian-newspapers/message-types'

interface SpecialEditionHomeProps {
    edition: EditionV4DTO
    publicationTitle: string
    publication: Omit<EditionPublication, 'the_nightly'>
    lazyloadImages?: boolean
    onEvent: (event: AllEvents) => void
    isTravelEdition?: boolean
    heading?: string
    showFirstDivider?: boolean
}

export const SpecialEditionHome = ({
    edition,
    publicationTitle,
    publication,
    lazyloadImages,
    onEvent,
    heading,
    showFirstDivider = true,
}: SpecialEditionHomeProps) => {
    const mainImage = edition.mainImages && {
        img: scaleImage(edition.mainImages[0].src, 600, false),
        alt: edition.mainImages[0].altText ?? edition.mainImages[0].captionText,
    }

    const isTravelEdition = publication === 'the_nightly_travel'
    const isLatestEdition = publication === 'the_nightly'

    // The title for "The Nightly On" editions is in the format "title_DD-MM-YYYY", this
    // removes the date part and normalizes the title to be capitalized.
    // Travel edition uses a fixed heading, so we use that if provided.
    const normalizedEditionTitle = heading
        ? heading
        : edition.title
        ? edition.title.split('_')[0].charAt(0).toUpperCase() +
          edition.title.split('_')[0].slice(1).toLowerCase() +
          ' Edition'
        : ''

    const handleImageClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName: 'TheNightlyOn_ImageButton',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: 'thenightly_on_cta',

                        linkText: 'Image Clicked',
                        linkUrl: edition.primaryTopic
                            ? `${edition.primaryTopic}`
                            : '/on',
                    },
                }),
            )
    }

    const subHeading = isTravelEdition || isLatestEdition
        ? format(new Date(edition.publicationDate), 'd MMMM yyyy')
        : 'In this Edition'

    return (
        <>
            {showFirstDivider && <StyledDivider />}
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        {!isTravelEdition && !isLatestEdition && (
                            <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        )}
                        <StyledNightlyEditionHeading>
                            {isTravelEdition
                                ? normalizedEditionTitle.toUpperCase()
                                : normalizedEditionTitle}
                        </StyledNightlyEditionHeading>
                        {isTravelEdition && (
                            <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        )}
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            {subHeading}
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {edition.teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <EditionsNightlyButtons
                            edition={edition}
                            heading={heading}
                            onEvent={onEvent}
                        />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionPictureRight>
                        <WebLink
                            to={edition.editionWebUrl}
                            onClick={handleImageClick}
                        >
                            {mainImage && (
                                <picture>
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpointMax('sm', false)}
                                    />
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpoint('sm', false)}
                                    />
                                    <img
                                        src={mainImage.img}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                        }}
                                        alt={mainImage.alt}
                                        loading={
                                            lazyloadImages ? 'lazy' : undefined
                                        }
                                    />
                                </picture>
                            )}
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}
